import { AllowanceStep } from '@app/pages/allowance/allowance';
import { AllowanceCalculatorBlocks } from '@app/shared/utils/enums/allowance-calculator-blocks.enum';
import { Injectable } from '@angular/core';
import { POLetterFrom, SpeciesCode, Tool } from '@app/shared/utils';
import { AppState } from '@app/store';
import { select, Store } from '@ngrx/store';
import {
  getAllowanceRecommendation,
  getAllowanceRecommendationAllTreatsQuantity,
  getAllowanceRecommendationAllWetQuantity,
  setAllowanceCurrentBlockEditing,
} from './actions/allowance-recommendation.actions';
import {
  selectCurrentRecommendation,
  selectPreviousRecommendationKcalAllocation,
  selectLoadingRecommendation,
  selectCurrentRecommendationKcalAllocation,
  selectBlockCurrentlyEditing,
  selectDefaultRecommendationKcalAllocation,
  selectAllowanceStep,
  selectWetQuantityPerDay,
  selectGestationRecommendation,
  selectTreatsQuantityPerDay,
} from './selectors/allowance.selectors';
import { shareAllowanceRecommendation } from './actions/allowance-share.actions';
import {
  convertAllowanceFlow,
  createAllowanceConsultation,
  createAllowanceConsultationFromShareRecoPopin,
  resetAllowanceFlow,
} from './actions/allowance-consultation.actions';
import { resetAllowanceParams, setAllowanceStep } from './actions/allowance-steps.actions';
import {
  selectNavigationRoutes,
  selectAllowanceParams,
  selectPetHasGrowthChart,
  selectDeprecatedPetInfo,
  selectIsGestatingSpeciesNonWL,
  selectIsNeuteredToday,
  selectIsPetInLactation,
  selectNormalAdult,
  selectProductSelectionIncentive,
  selectUnderweightAdult,
  selectIncentiveTool,
} from './selectors/allowance-params.selectors';
import { Clinic, PetOwner, Product } from '@app/core/models';
import { createAllowanceConsultationVetSource } from './actions/allowance-vetsource.actions';
import { SystemPreferenceCode } from '@app/shared/utils/enums/system-preference-code';
import { SaveConsultationPopinFormValuesOutput } from '@app/shared/components/share-reco-popin/share-reco-popin';

@Injectable()
export class AllowanceFacade {
  /** allowance-params.selectors */
  allowanceParams$ = this.store$.select(selectAllowanceParams);
  navigationRoutes$ = this.store$.select(selectNavigationRoutes);
  productSelectionIncentive$ = this.store$.select(selectProductSelectionIncentive);
  petHasGrowthChart$ = (currentSystemPreferenceCode: SystemPreferenceCode) =>
    this.store$.select(selectPetHasGrowthChart(currentSystemPreferenceCode));
  isNeuteredToday$ = this.store$.select(selectIsNeuteredToday);
  deprecatedPetInfo$ = this.store$.select(selectDeprecatedPetInfo);
  isPetInLactation$ = this.store$.select(selectIsPetInLactation);
  isGestatingSpeciesNonWL$ = (selectedProducts: Product[], speciesCode: SpeciesCode) =>
    this.store$.select(selectIsGestatingSpeciesNonWL(selectedProducts, speciesCode));
  underweightAdult$ = (selectedProducts: Product[]) => this.store$.select(selectUnderweightAdult(selectedProducts));
  normalAdult$ = (selectedProducts: Product[]) => this.store$.select(selectNormalAdult(selectedProducts));
  incentiveTool$ = this.store$.select(selectIncentiveTool);

  /** allowance.selectors */
  currentRecommendation$ = this.store$.pipe(select(selectCurrentRecommendation));
  gestationRecommendation$ = this.store$.pipe(select(selectGestationRecommendation));
  currentRecommendationKcalAllocation$ = this.store$.pipe(select(selectCurrentRecommendationKcalAllocation));
  defaultRecommendationKcalAllocation$ = this.store$.pipe(select(selectDefaultRecommendationKcalAllocation));
  previousRecommendationKcalAllocation$ = this.store$.pipe(select(selectPreviousRecommendationKcalAllocation));
  loadingRecommendation$ = this.store$.pipe(select(selectLoadingRecommendation));
  blockCurrentlyEditing$ = this.store$.pipe(select(selectBlockCurrentlyEditing));
  allowanceStep$ = this.store$.pipe(select(selectAllowanceStep));
  wetQuantityPerDay$ = this.store$.pipe(select(selectWetQuantityPerDay));
  treatsQuantityPerDay$ = this.store$.pipe(select(selectTreatsQuantityPerDay));

  constructor(private readonly store$: Store<AppState>) {}

  /** allowance-consultation.actions */
  createAllowanceConsultation(): void {
    this.store$.dispatch(createAllowanceConsultation({}));
  }

  createAllowanceConsultationFromShareRecoPopin(formValues?: SaveConsultationPopinFormValuesOutput): void {
    this.store$.dispatch(createAllowanceConsultationFromShareRecoPopin({ formValues }));
  }

  convertAllowanceFlow(flow: Tool): void {
    this.store$.dispatch(convertAllowanceFlow({ flow }));
  }

  resetAllowanceFlow(coreTool: Tool): void {
    this.store$.dispatch(resetAllowanceFlow({ coreTool }));
  }

  /** allowance-recommendation.actions */
  getRecommendation(): void {
    this.store$.dispatch(getAllowanceRecommendation({}));
  }

  getManualRecommendationWithAllocation(allocation: number): void {
    this.store$.dispatch(getAllowanceRecommendation({ allocation }));
  }

  getManualRecommendationWithWetRation(value: number): void {
    this.store$.dispatch(getAllowanceRecommendation({ manualWetRation: value }));
  }

  getManualRecommendationWithTreatsRation(value: number): void {
    this.store$.dispatch(getAllowanceRecommendation({ manualTreatsRation: value }));
  }

  getAllowanceRecommendationAllWetQuantity(allocation: number): void {
    this.store$.dispatch(getAllowanceRecommendationAllWetQuantity({ allocation }));
  }

  getAllowanceRecommendationAllTreatsQuantity(allocation: number): void {
    this.store$.dispatch(getAllowanceRecommendationAllTreatsQuantity({ allocation }));
  }

  setAllowanceCurrentBlockEditing(block: AllowanceCalculatorBlocks, value: boolean): void {
    this.store$.dispatch(setAllowanceCurrentBlockEditing({ block, value }));
  }

  /** allowance-share.actions */
  shareAllowanceRecommendation(value: POLetterFrom): void {
    this.store$.dispatch(shareAllowanceRecommendation({ value }));
  }

  /** allowance-steps.actions */
  setAllowanceStep(step: AllowanceStep, tool?: Tool): void {
    this.store$.dispatch(setAllowanceStep({ step, tool }));
  }

  resetAllowanceParams(): void {
    this.store$.dispatch(resetAllowanceParams());
  }

  /** allowance-vetsource.actions */
  createAllowanceConsultationVetSource(email: string, owner: PetOwner, clinic: Clinic): void {
    this.store$.dispatch(createAllowanceConsultationVetSource({ email, owner, clinic }));
  }
}
